import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@clay-run/glaze/button';
import { Icon } from '@clay-run/glaze/icon';
import { IconButton } from '@clay-run/glaze/iconbutton';
import { Tab, TabList, Tabs } from '@clay-run/glaze/tabs';
import { Text } from '@clay-run/glaze/text';

import { FLOW_ICONS } from '../../icons';
import { useEnrollQueue, type SidebarTab } from '../../EnrollQueue/EnrollQueueContext';
import { useDockUrlSync } from '../../EnrollQueue/useDockUrlSync';
import { useDockSendHandlers } from '../../EnrollQueue/useDockSendHandlers';
import { useEnrollQueuePanelWidthCss } from '../../EnrollQueue/panel-width-store';
import { PlaysPane } from './PlaysPane';
import { ActionsIndex } from './ActionsIndex';
import { ActionsDetail } from './ActionsDetail';
import { InvitePane } from './InvitePane';
import { NotesPane } from '../notes/NotesPane';
import { useFlowNotesLive } from '../notes/useFlowNotesLive';
import { useNotesUrlSync } from '../notes/useNotesUrlSync';
import { useNotesPendingConfirmation } from '../notes/useFlowNotes';
import { useNotesEnabled } from '../../../../hooks/useNotesEnabled';
import { RailResizeHandle } from './RailResizeHandle';
import { RAIL_PANE_PADDING } from './RailSearchRow';
import { useFlowPanelsActionsNode } from '../panels/flow-panels-slots';
import { useShellFrameStore } from '../shell-frame-store';
import { useActionsDockSync } from '../panels/useActionsDockSync';
import { usePanelTogglesHost } from '../panels/usePanelTogglesHost';
import { PanelToggles } from '../PanelToggles';

/**
 * The enroll-queue rail (plan 2026-09-30-002, D37–D40) — the only dock now:
 * the V1 `EnrollQueueSidebar` icon-rail dock and the retired flag switch
 * (`EnrollQueue/EnrollQueueDock.tsx`) that chose between the two are gone.
 * This is `useDockUrlSync`'s one and only caller, which is what keeps its
 * "exactly one caller" contract true.
 *
 * What this shell is:
 *  - a Glaze text tab strip (`Plays · Actions · Invite`, D8). No count
 *    badge on Plays and no live blip on Actions (D40) — the prototype's strip
 *    has neither.
 *  - U14 — the tab strip is not the only thing that can occupy that 48px
 *    row: once a request is selected on the Actions tab (`selectedRequestId
 *    !== null` while `activeTab === 'requests'`, `detailOpen` below), it's
 *    replaced wholesale by a detail-mode strip (`‹ Back to Actions` + the
 *    same `✕`) — there is no tab row and no tab strip underneath it while a
 *    detail is open, matching the `sc-terra-flow` prototype's
 *    `RailPanel.tsx`. `ActionsDetail` itself renders no back/close affordance
 *    of its own (see that component's header) — this strip is the only one.
 *    Bug fix (2026-10-01): `ActionsIndex` stays MOUNTED (hidden via a plain
 *    `hidden` wrapper div, not unmounted) underneath `ActionsDetail` — a
 *    type-swapping ternary here used to fully unmount it on every detail
 *    open, wiping its local search/scope/contactIdFilter state each time.
 *    Do not revert this back to a ternary; the hidden wrapper must carry no
 *    scroll/overflow class of its own (see the "one shared scroller" note
 *    below).
 *  - NO hoisted omnibox and no nested search-results surface (D13, D37):
 *    each tab filters itself. Actions and Invite already carry their own
 *    inputs; Plays gets its client-side filter with `PlaysPane` in U2. There
 *    is deliberately no filter row in this file at all — U1's rail has none
 *    by design, which is why the body dispatch below has no search branch.
 *  - NO FAB (D15). On `/flow/*` the dock opens from `shell-next/Header.tsx`'s
 *    "Actions panel" toggle (which writes the panel store's `actionsOpen`,
 *    mirrored onto this dock's `isOpen` by `useActionsDockSync` below) and
 *    from every existing `openQueueTab()` / `openChat()` /
 *    `openCustomInvite()` / `openRequestDetail()` / `?request=` producer —
 *    which, through the same hook's other direction, now also LIGHTS that
 *    toggle instead of popping the rail open behind an unlit icon (R6).
 *    Off `/flow` the producers are still the only opener. While the dock is
 *    open in push mode that same `PanelToggles` cluster sits in THIS strip
 *    (next bullet) and is what closes it.
 *  - PanelToggles whenever this column is the shell's top-right corner —
 *    docked in push mode (Spencer QA round 3; round 2 hosted them only while
 *    Flow was collapsed). See the inline comment at `usePanelTogglesHost()`
 *    below and `shell-next/PanelToggles.tsx`'s header for the
 *    one-visible-place rule.
 *
 * Hook order is load-bearing, and every hook runs BEFORE the
 * `if (!isOpen) return null` below: `useDockUrlSync` has to be able to switch
 * the tab and open the dock for a pasted `?request=` link, which
 * it cannot do from a component that returns early while closed (see that
 * hook's header and `docs/flow/request-deep-links.md`). `useDockSendHandlers`
 * is called EXACTLY ONCE here for the same reason V1 calls it once — a second
 * call would stand up a second `useSendGroupPipeline` over the same items.
 *
 * TWO render targets, one component (plan 2026-09-30-005's slot contract):
 *  - DOCKED — `useFlowPanelsActionsNode()` is non-null while the frame's
 *    `panels/FlowPanels.tsx` has its Actions column open. The rail's content
 *    is `createPortal`ed into that column. No `RailResizeHandle` and no
 *    `width` style here: the column's own `flow-actions` divider IS its
 *    resize handle and `flow-panels-store.ts`'s share is its width model;
 *    a second handle would fight it and a second (px) width would be ignored
 *    or wrong. No `border-l` either — `DividerFrame`/`overlayRightProps`
 *    already draw the column's edge.
 *  - FALLBACK — only where no `FlowPanels` is mounted (Delphi, the one
 *    frameless route; `useShellFrameStore().frameActive` is false): V1's `fixed top-0 right-0` `<aside>`, verbatim,
 *    with the rep-draggable `panel-width-store.ts` width and its own
 *    `RailResizeHandle`. `QueueContentShift` (`AppRouter.tsx`) pads the page
 *    against this geometry (D38) and pads 0 while docked.
 * `EnrollQueueDock` and the AppRouter mount point do not change — only
 * where this component's output lands.
 */

/**
 * `Record<SidebarTab, …>` on purpose (V1's `TAB_META` precedent): widening
 * `SidebarTab` forces a compile error here until the new tab gets a label.
 * The three `<Tab>`s below are written out in D8's order. Chat is not a tab:
 * it has its own panel.
 */
const RAIL_TAB_LABELS: Record<SidebarTab, string> = {
  queue: 'Plays',
  requests: 'Actions',
  customInvite: 'Invite',
  notes: 'Notes',
};

export function RailDock() {
  // U2d — the Plays tab's whole data surface moved into `PlaysPane`, which
  // reads `useEnrollQueue()` itself (D39). This shell keeps only what the
  // CHROME needs: the tab row (or the detail-mode strip, U14), the close
  // button, the read-only line.
  const { isOpen, close, readOnly, isDemo, activeTab, setActiveTab, selectedRequestId, closeRequestDetail, requestsSearchSeed } = useEnrollQueue();

  const { enabled: showNotesTab } = useNotesEnabled();

  useDockUrlSync();
  // R6 — the one bridge between this dock's `isOpen` and the Flow shell's
  // `actionsOpen` panel flag, active on every framed route. Mounted here (not in
  // `FlowShell`) because this is the component that is mounted under V2
  // regardless of route and already has `useEnrollQueue()` in scope; see that
  // hook's doc comment for the open-only/declaration-order rules.
  useActionsDockSync();
  // `?notes=` deep link (U9c): its own hook, behind the Notes flag, never folded into
  // `useDockUrlSync`. Mounted regardless of `activeTab` so a pasted link can open the dock.
  useNotesUrlSync();
  // Notes (U7c): once per dock, whatever tab is showing. `useFlowNotesLive`
  // keeps thread/summary caches fresh from the shared SSE stream;
  // `useNotesPendingConfirmation` owns the post-confirmation re-checks. Neither
  // may be mounted by the pane or composer (the pane unmounts off-tab).
  // `useResetNotesStoreOnIdentityChange` is intentionally absent: an
  // impersonation switch reloads the app, so the store resets with it.
  useFlowNotesLive();
  useNotesPendingConfirmation();
  const handlers = useDockSendHandlers();
  const width = useEnrollQueuePanelWidthCss();
  const actionsNode = useFlowPanelsActionsNode();
  const framed = useShellFrameStore((s) => s.frameActive);
  // Spencer QA round 3 — the three mode toggles pin to the top-right corner
  // of the WHOLE shell, so whenever this column is the rightmost one (docked
  // in push mode, `derivePanelTogglesHost` → 'actions'; round 2 did this
  // only while Flow was collapsed) this strip hosts the SAME `PanelToggles`
  // at its right edge, flush with the Actions column's edge, and the Flow
  // header drops its copy. Gated on `actionsNode` too: the store can read
  // `actionsOpen` off `/flow` while the fallback aside is open, and there is
  // no Flow shell there to toggle. Overlay mode never resolves to 'actions'
  // (the floating panel starts UNDER the full-width Flow header), so the
  // `actionsNode` the overlay panel also publishes does not host them.
  // Declared before the `!isOpen` early return like every other hook here.
  const togglesHost = usePanelTogglesHost();

  const panelRef = useRef<HTMLElement | null>(null);

  // Codex round (2026-10-01), bug #1 — a NEW `requestsSearchSeed` (a "View
  // all" click from a DIFFERENT contact's Recent Actions) must close
  // whatever request detail happens to already be open, so the rep sees the
  // newly-filtered list they just asked for instead of a stale, unrelated
  // detail hidden on top of it. `closeRequestDetail` sets `selectedRequestId`,
  // which is owned by `EnrollQueueProvider` — an ANCESTOR of this component —
  // so this cannot be a render-phase adjustment the way `useCustomInviteForm`'s
  // `seededForPlayRef` guard is: that pattern only works for a component's
  // OWN local `useState` setter called during ITS OWN render (React's
  // documented self-bailout); calling an ancestor's setter during a
  // descendant's render trips React's "Cannot update a component while
  // rendering a different component" guard. An effect is the safe seam for
  // touching foreign state, but a NAIVE `useEffect(() => {...},
  // [requestsSearchSeed])` that also reads `selectedRequestId` (required by
  // exhaustive-deps once `selectedRequestId` is read inside) would re-fire on
  // every LATER `selectedRequestId` change too — e.g. the rep opening a row
  // from the freshly-filtered list would immediately get its own detail
  // closed again, because the effect reruns and `requestsSearchSeed` is still
  // truthy. The ref below tracks which seed object this effect has already
  // reacted to (seed is a fresh object per `openRequestsForContact` call —
  // see that context field's doc comment) so the close only fires once, on
  // the render where the seed itself actually changed — never on a
  // subsequent `selectedRequestId` change alone.
  const reactedSeedRef = useRef(requestsSearchSeed);
  useEffect(() => {
    if (requestsSearchSeed !== reactedSeedRef.current) {
      reactedSeedRef.current = requestsSearchSeed;
      if (requestsSearchSeed && selectedRequestId !== null) {
        closeRequestDetail();
      }
    }
  }, [requestsSearchSeed, selectedRequestId, closeRequestDetail]);

  // Codex round (2026-10-01), bug #2 — Plays/Actions/Invite share ONE
  // `overflow-y-auto` scroller (see the body-dispatch comment below); if the
  // rep scrolls the Actions list down and then opens a detail, that same
  // scroller keeps its old offset and the detail renders mid-scroll, header
  // pushed off the top. Reset to 0 whenever a detail is NEWLY opened — a
  // `null` → id transition (the common case), or an id → DIFFERENT id
  // transition (StatePills.tsx / EntityStateFactCard.tsx can call
  // `openRequestDetail` directly from outside this tab while one detail is
  // already open, jumping straight to another) — but never when the id goes
  // back to `null` (returning to the index must not touch its scroll
  // position) and never on an unrelated re-render that leaves the same id in
  // place.
  const actionsScrollRef = useRef<HTMLDivElement | null>(null);
  const previousSelectedRequestIdRef = useRef(selectedRequestId);
  useEffect(() => {
    if (selectedRequestId !== null && selectedRequestId !== previousSelectedRequestIdRef.current) {
      const el = actionsScrollRef.current;
      if (el) el.scrollTop = 0;
    }
    previousSelectedRequestIdRef.current = selectedRequestId;
  }, [selectedRequestId]);

  if (!isOpen) return null;

  // U14 — Actions-with-a-selection replaces the whole tab row with a
  // detail-mode strip (prototype's `RailPanel.tsx` `detailOpen`). Any other
  // tab, or Actions with nothing selected, keeps the normal four-tab strip.
  const detailOpen = activeTab === 'requests' && selectedRequestId !== null;

  const content = (
    <>
      {/* Item g (rail-glaze-parity QA round cleanup) — one shared h-12 strip
          container for BOTH the detail-mode back link and the ordinary tab
          row, differing only in which left-half renders. The trailing
          `IconButton` (V1's exact "Close enroll queue" accessible name) used
          to render in EITHER mode; U3 (R12) made it fallback-only — see the
          comment just above that button. Tested by query-by-role/label, not
          container class — a test failure here is real behavior change, not
          a false positive from this hoist. R8: `px-3` so this strip's band
          aligns with `Header.tsx`'s and `ChatHeaderV2.tsx`'s. */}
      <div className="flex h-12 shrink-0 items-center justify-between gap-2 border-b-[0.5px] border-border-primary px-3">
        {detailOpen ? (
          <Button size="sm" variant="ghost" onClick={closeRequestDetail}>
            <Icon type={FLOW_ICONS.back} size="sm" aria-hidden />
            Back to Actions
          </Button>
        ) : (
          // Tab row — `shell-next/Header.tsx`'s `Tabs` grammar at the same 48px
          // height, so the rail's strip and the shell header's strip read as one
          // system. `overflow-x-auto` (Spencer QA round 3): with `PanelToggles`
          // hosted at this strip's right edge whenever Actions is open, three `sm`
          // tabs + the 92px cluster + gaps + `px-3` need ~352px, and the
          // column is ~342px at a 1440px viewport's default quarter share and
          // 320px at its floor. Glaze's `TabList` neither wraps nor scrolls, so
          // the strip scrolls its tabs instead of sliding the last one under the
          // toggles — bare `overflow-x-auto`, the `table/LedgerToolbar.tsx` /
          // `ui/Module.tsx` `scrollable` precedent, no hidden-scrollbar hack.
          <Tabs<SidebarTab>
            value={activeTab}
            onValueChange={setActiveTab}
            size="sm"
            layout="fit"
            className="min-w-0 flex-1 self-stretch overflow-x-auto"
          >
            <TabList className="h-full">
              <Tab value="queue">{RAIL_TAB_LABELS.queue}</Tab>
              <Tab value="requests">{RAIL_TAB_LABELS.requests}</Tab>
              <Tab value="customInvite">{RAIL_TAB_LABELS.customInvite}</Tab>
              {showNotesTab && <Tab value="notes">{RAIL_TAB_LABELS.notes}</Tab>}
            </TabList>
          </Tabs>
        )}
        {actionsNode && togglesHost === 'actions' && <PanelToggles />}
        {/* U3 (R12) — the X renders ONLY in fallback (off-`/flow`) mode now.
            Docked (`actionsNode` set): the Header's lightning Actions toggle
            is the one close control — toggling it off flips the panel
            store's `actionsOpen`, which `useActionsDockSync`'s Effect B reads
            and calls this same `close()` with, and `useDockUrlSync`'s WRITE
            effect clears `?request=` right behind it, so an X here would be a
            second way to do the same thing. Fallback: the
            X stays, because off `/flow` there is no toggle to close it — V1's
            exact accessible name, "Close enroll queue", not "Close the rail". */}
        {!actionsNode && (
          <IconButton
            variant="ghost"
            size="sm"
            icon={FLOW_ICONS.close}
            aria-label="Close enroll queue"
            onClick={close}
          />
        )}
      </div>

      {readOnly && !isDemo && (
        <Text size="xs" variant="critical" className="px-3 py-1">
          Read-only while impersonating
        </Text>
      )}

      {/* Body dispatch (D37, U14). No `searchSurface` branch — V2 has no
          omnibox, so there is no results surface to render over the active
          tab. Plays, Actions (index or detail) and Invite
          share exactly ONE scroller — not one each — because that's the
          nearest scrolling ancestor `PlaysPane`'s sticky `PlaySection`
          headers pin against (D2), and none of `ActionsIndex` /
          `ActionsDetail` / `InvitePane` render a scroll container of their
          own; a second one here would be a competing scroller, not a nested
          convenience. */}
      <div ref={actionsScrollRef} className={`flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain ${RAIL_PANE_PADDING}`}>
        {activeTab === 'queue' && <PlaysPane handlers={handlers} />}
        {activeTab === 'requests' && (
          <>
            <div className={selectedRequestId !== null ? 'hidden' : undefined}>
              <ActionsIndex requestsSearchSeed={requestsSearchSeed ?? undefined} />
            </div>
            {selectedRequestId !== null && <ActionsDetail requestId={selectedRequestId} />}
          </>
        )}
        {activeTab === 'customInvite' && <InvitePane />}
        {showNotesTab && activeTab === 'notes' && <NotesPane />}
      </div>
    </>
  );

  if (actionsNode) {
    return createPortal(
      <div
        role="complementary"
        aria-label="Enroll queue"
        data-flow-rail-docked
        className="flex min-h-0 flex-1 flex-col overflow-hidden bg-bg-primary"
      >
        {content}
      </div>,
      actionsNode,
    );
  }

  // Framed but the column's slot node has not been published yet (callback ref
  // fires after first paint on a cold `?panels=actions` load, or the column is
  // closed/suppressed): render nothing rather than flash the fixed aside.
  if (framed) return null;

  return (
    <aside
      ref={panelRef}
      role="complementary"
      aria-label="Enroll queue"
      // Inline style, not a Tailwind arbitrary class: the width is the
      // rep-draggable store value every coupled call site reads
      // (`panel-width-store.ts`'s "four call sites that MUST stay coupled"),
      // and a template-composed arbitrary class isn't JIT-scannable.
      style={{ width }}
      // `fixed top-0 right-0 h-full max-w-full` + `z-50` are V1's positioning
      // classes verbatim (`EnrollQueueSidebar.tsx`'s panel) — `QueueContentShift`
      // pads the main content against exactly this geometry. V1's
      // slide-in `transition-transform`/`translate-x-full` pair has no
      // counterpart here: this shell unmounts when closed (`!isOpen` above),
      // so there is nothing to slide off-screen.
      className="fixed top-0 right-0 h-full max-w-full z-50 flex flex-col bg-bg-primary border-l-[0.5px] border-border-primary"
    >
      {/* D7: always-on, every tab. Must be the FIRST child and
          rely on this `fixed` panel as its containing block (it is
          `absolute inset-y-0 left-0`). */}
      <RailResizeHandle panelRef={panelRef} />

      {content}
    </aside>
  );
}
